import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuthStore } from '@stores/authStore';
import { createAppReviewListener, type AppReviewNotification } from '@lib/services/firebase/appReviewListener';

export const useAppReviewNotification = () => {
  const { user, isAuthenticated } = useAuthStore();
  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const [currentNotification, setCurrentNotification] = useState<AppReviewNotification | null>(null);
  const listenerRef = useRef<{ unsubscribe: () => void } | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      return;
    }

    console.log('Creating app review listener for user:', user.id);

    // Create async function to handle the listener creation
    const setupListener = async () => {
      try {
        const listener = await createAppReviewListener({
          userId: user.id,
          onNotification: (notification) => {
            console.log('Received app review notification:', notification);
            
            console.log('Showing app review popup for notification:', notification.id);
            setCurrentNotification(notification);
            setShowReviewPopup(true);
          },
          onError: (error) => {
            // Don't log permission errors as they're expected for some users
            if (error.code === 'permission-denied' || error.message.includes('Missing or insufficient permissions')) {
              console.warn('App review permission denied. This is normal for users without app review access.');
              return;
            }
            console.error('App review listener error:', error);
          },
        });

        listenerRef.current = listener;
      } catch (error) {
        // Don't log permission errors as they're expected for some users
        if (error instanceof Error && error.message.includes('Missing or insufficient permissions')) {
          console.warn('App review setup permission denied. This is normal for users without app review access.');
          return;
        }
        console.error('Failed to create app review listener:', error);
      }
    };

    setupListener();

    // Cleanup function
    return () => {
      console.log('Cleaning up app review listener');
      if (listenerRef.current) {
        listenerRef.current.unsubscribe();
        listenerRef.current = null;
      }
    };
  }, [user?.id, isAuthenticated]); // Only depend on user and auth status

  const handleClosePopup = useCallback(() => {
    console.log('handleClosePopup called');
    
    // Stop listening for new notifications FIRST
    if (listenerRef.current) {
      console.log('Unsubscribing from app review notifications');
      listenerRef.current.unsubscribe();
      listenerRef.current = null;
    }
    
    // Close popup
    setShowReviewPopup(false);
  }, []);

  const handleSubmitRating = useCallback((rating: number) => {
    console.log('handleSubmitRating called with rating:', rating);
    
    // Stop listening for new notifications FIRST
    if (listenerRef.current) {
      console.log('Unsubscribing from app review notifications after submit');
      listenerRef.current.unsubscribe();
      listenerRef.current = null;
    }
    
    // Close popup
    setShowReviewPopup(false);
  }, []);

  return {
    showReviewPopup,
    currentNotification,
    onClosePopup: handleClosePopup,
    onSubmitRating: handleSubmitRating,
  };
};
